import { Download, Trash2 } from 'lucide-react';
import { useDownloads } from '@/features/downloads/DownloadProvider';
import { removeLocalMedia } from '@/services/media/manualImport';
import { mediaWriter } from '@/storage/index';
import type { LocalSignState, Sign } from '@/types/catalog';

interface Props {
  sign: Sign;
  state: LocalSignState | undefined;
}

/**
 * Baixar ou remover a mídia de um sinal (F8-T06, F12-T17).
 *
 * Não aparece quando não há origem de mídia configurada: um botão que sempre falha
 * é pior que nenhum botão. Nesse caso o estado "Somente online" já explica a
 * situação (F4-T11).
 */
export function DownloadButton({ sign, state }: Props): React.ReactElement | null {
  const { canDownload, queueSign, cancel } = useDownloads();

  const status = state?.offline.status ?? 'none';
  const available = status === 'available';

  if (!available && !canDownload(sign)) return null;

  if (available) {
    return (
      <button
        type="button"
        onClick={() => void removeLocalMedia(sign.id, mediaWriter())}
        className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-800 text-sm font-medium text-slate-300 active:bg-slate-700"
      >
        <Trash2 size={18} aria-hidden />
        Remover download
      </button>
    );
  }

  if (status === 'queued' || status === 'downloading') {
    return (
      <button
        type="button"
        onClick={() => void cancel(sign.id)}
        className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-800 text-sm font-medium text-slate-300 active:bg-slate-700"
      >
        {status === 'downloading' ? 'Cancelar download' : 'Sair da fila'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void queueSign(sign)}
      className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-sky-500/15 text-sm font-medium text-sky-300 active:bg-sky-500/25"
    >
      <Download size={18} aria-hidden />
      {status === 'error' ? 'Tentar de novo' : 'Baixar'}
    </button>
  );
}
